import {
  COMPETITIVE_KITS,
  KITS,
  ONLINE_MODE_IDS,
  TIERS,
  WORLD_METRICS,
  WORLD_MIN_MATCHES,
  WORLD_PERIODS,
  tierFor,
  type OnlineModeId,
  type TierId,
  type WorldBadge,
  type WorldCard,
  type WorldMetric,
  type WorldPeriod,
  type WorldRecord,
  type WorldRow,
  type WorldSeasonPodium,
  type WorldTotals,
  type WorldWeekWinner,
} from "@golazo/shared";
import { lang, t } from "../i18n/index.js";
import { ratePlayer } from "../game/rating.js";
import type { WorldBoardMsg, WorldService } from "../online/world.js";
import { escapeHtml, kitTokenHtml } from "./careerText.js";
import { TIER_LABEL, tierColor, tierLabel } from "./tierMark.js";
import "./worldScreen.css";

/**
 * ESTADISTICAS MUNDIALES: donde uno se mide con todos los demas.
 *
 * Todo lo que se ve aqui sale de partidos online que el servidor ha rehecho
 * el mismo (el arbitro): no del perfil que calcula el juego, que se podria
 * falsear. Por eso lo de contra la IA y la carrera no entra, y se dice.
 *
 * A la izquierda, tu sitio (division, puntuacion, puesto, percentil, quien
 * tienes delante y detras). A la derecha, la clasificacion con sus filtros,
 * los records del mundo y la escalera de divisiones. Tocar a cualquiera abre
 * su ficha publica, con un cara a cara contigo.
 */
export type WorldScreenAction = { type: "back" } | { type: "lobby" };

export interface WorldScreenOptions {
  service: WorldService;
  isFriend?: (clientId: string) => boolean;
  addFriend?: (clientId: string) => void;
}

export interface WorldScreenHandle {
  show(): Promise<WorldScreenAction>;
  hide(): void;
  /** Salir porque se va a otro sitio desde fuera (un aviso de un amigo). */
  leave(): void;
  dispose(): void;
}

type Tab = "board" | "records" | "champions" | "tiers";

/**
 * Las pastillas, en MAYUSCULAS a proposito: son claves de `t()` propias.
 * "Media" y "Victorias" ya existian en ingles con otro sentido ("Rating",
 * "Win rate") y aqui chocaban. Para las frases, `METRIC_NAME`.
 */
const METRIC_LABEL: Record<WorldMetric, string> = {
  mmr: "PUNTUACIÓN",
  overall: "MEDIA",
  goals: "GOLES",
  assists: "ASISTENCIAS",
  mvps: "MVP",
  wins: "VICTORIAS",
  winRate: "% VICTORIAS",
  matches: "PARTIDOS",
};
const METRIC_NAME: Record<WorldMetric, string> = {
  mmr: "puntuación",
  overall: "la media",
  goals: "goles",
  assists: "asistencias",
  mvps: "MVP",
  wins: "victorias",
  winRate: "% de victorias",
  matches: "partidos",
};
const PERIOD_LABEL: Record<WorldPeriod, string> = { week: "Semana", season: "Temporada", all: "Siempre" };
const RECORD_LABEL: Record<WorldRecord["id"], { icon: string; title: string }> = {
  goals: { icon: "⚽", title: "Más goles en un partido" },
  assists: { icon: "🎯", title: "Más asistencias en un partido" },
  biggestWin: { icon: "💥", title: "La mayor goleada" },
  score: { icon: "⭐", title: "El mejor partido" },
  winStreak: { icon: "🔥", title: "La racha más larga" },
  mvps: { icon: "👑", title: "Más veces MVP" },
};

/** Lo que guarda la barra del menu principal: tu division y puesto, para enseñarlos sin preguntar. */
export const WORLD_TEASER_KEY = "fynta.world.teaser";


function tierBadgeHtml(tier: TierId | null, size: "s" | "m" | "l" = "m"): string {
  return `<span class="ws-tier ws-tier--${size}" style="--tier:${tierColor(tier)}" title="${escapeHtml(tierLabel(tier))}">${tier ? escapeHtml(t(TIER_LABEL[tier]).charAt(0)) : "?"}</span>`;
}

function formatValue(metric: WorldMetric, value: number): string {
  return metric === "winRate" ? `${value} %` : String(value);
}

function seasonName(season: string): string {
  const [y, m] = season.split("-").map(Number);
  if (!y || !m) return season;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(lang() === "es" ? "es-ES" : "en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
}

function untilText(at: number, now = Date.now()): string {
  const ms = Math.max(0, at - now);
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  if (days > 0) return t("{d} d {h} h", { d: days, h: hours });
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  return t("{h} h {m} min", { h: hours, m: minutes });
}

function dateText(at: number): string {
  return new Date(at).toLocaleDateString(lang() === "es" ? "es-ES" : "en-GB", { day: "numeric", month: "short" });
}

/** Una insignia, en palabras. */
export function badgeInfo(b: WorldBadge): { icon: string; title: string; desc: string } {
  if (b.id === "tier") return { icon: "🛡️", title: t("División {tier}", { tier: tierLabel(b.tier ?? null) }), desc: t("Tu división esta temporada, contra personas.") };
  if (b.id === "season") return { icon: "🏆", title: t("Mejor temporada: {tier}", { tier: tierLabel(b.tier ?? null) }), desc: t("Hasta donde llegó en una temporada ya cerrada.") };
  if (b.id.startsWith("top-")) {
    const metric = b.id.slice(4) as WorldMetric;
    const icon = b.value === 1 ? "🥇" : b.value === 2 ? "🥈" : b.value === 3 ? "🥉" : "🏅";
    return { icon, title: t("Top {n} mundial · {metric}", { n: b.value ?? 0, metric: t(METRIC_NAME[metric] ?? metric) }), desc: t("Puesto en la clasificación de siempre.") };
  }
  if (b.id.startsWith("record-")) {
    const rec = RECORD_LABEL[b.id.slice(7) as WorldRecord["id"]];
    return { icon: "🌍", title: t("Récord del mundo"), desc: rec ? `${t(rec.title)}: ${b.value}` : "" };
  }
  if (b.id === "week") return { icon: "👑", title: t("Campeón de la semana ×{n}", { n: b.value ?? 1 }), desc: t("El que más ganó una semana (con 3 partidos o más).") };
  if (b.id === "streak") return { icon: "🔥", title: t("Racha de {n}", { n: b.value ?? 0 }), desc: t("Victorias seguidas online.") };
  if (b.id === "hattrick") return { icon: "⚽", title: t("Hat-tricks ×{n}", { n: b.value ?? 0 }), desc: t("Partidos con tres goles o más.") };
  if (b.id === "veteran") return { icon: "🎖️", title: t("Veterano"), desc: t("{n} partidos online.", { n: b.value ?? 0 }) };
  if (b.id === "rival") return { icon: "⚔️", title: t("Competidor"), desc: t("{n} partidos contra personas esta temporada.", { n: b.value ?? 0 }) };
  return { icon: "🏅", title: b.id, desc: "" };
}

/** El radar de los seis ejes (de la media), con otro encima si se compara. */
function radarHtml(mine: WorldTotals, theirs: WorldTotals | null, theirsName: string): string {
  const a = ratePlayer(mine).attributes;
  const b = theirs ? ratePlayer(theirs).attributes : null;
  const size = 220;
  const c = size / 2;
  const r = 70;
  const n = a.length;
  const point = (i: number, value: number, scale = 1): [number, number] => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    const k = scale * Math.max(0.06, Math.min(1, (value - 38) / 61));
    return [c + Math.cos(angle) * r * k, c + Math.sin(angle) * r * k];
  };
  const poly = (values: number[]): string => values.map((v, i) => point(i, v).join(",")).join(" ");
  const rings = [0.33, 0.66, 1]
    .map((k) => `<polygon class="ws-radar-ring" points="${a.map((_, i) => point(i, 99, k).join(",")).join(" ")}" />`)
    .join("");
  const axes = a
    .map((attr, i) => {
      const [x, y] = point(i, 99, 1);
      const [lx, ly] = point(i, 99, 1.3);
      return `<line class="ws-radar-axis" x1="${c}" y1="${c}" x2="${x}" y2="${y}" /><text class="ws-radar-label" x="${lx}" y="${ly}">${escapeHtml(attr.label)} ${attr.value}</text>`;
    })
    .join("");
  return `
    <figure class="ws-radar">
      <svg viewBox="0 0 ${size} ${size}" role="img" aria-label="${escapeHtml(t("Atributos"))}">
        ${rings}${axes}
        ${b ? `<polygon class="ws-radar-b" points="${poly(b.map((x) => x.value))}" />` : ""}
        <polygon class="ws-radar-a" points="${poly(a.map((x) => x.value))}" />
      </svg>
      ${b ? `<figcaption><span class="ws-legend is-a">${escapeHtml(theirsName)}</span><span class="ws-legend is-b">${escapeHtml(t("Tú"))}</span></figcaption>` : ""}
    </figure>`;
}

const per = (a: number, b: number): number => (b > 0 ? a / b : 0);

export function createWorldScreen(root: HTMLElement, opts: WorldScreenOptions): WorldScreenHandle {
  const { service } = opts;
  const overlay = document.createElement("div");
  overlay.id = "world-screen";
  overlay.className = "ws is-hidden";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-label", t("Estadísticas mundiales"));
  root.appendChild(overlay);

  const cardLayer = document.createElement("div");
  cardLayer.className = "ws-card-layer is-hidden";
  cardLayer.setAttribute("role", "dialog");
  cardLayer.setAttribute("aria-modal", "true");
  root.appendChild(cardLayer);

  let tab: Tab = "board";
  let metric: WorldMetric = "mmr";
  let period: WorldPeriod = "season";
  let friends = false;
  let mode: OnlineModeId | undefined;
  let board: WorldBoardMsg | null = null;
  let rows: WorldRow[] = [];
  let loading = false;
  let offline = false;
  let myCard: WorldCard | null | undefined;
  let records: WorldRecord[] | null = null;
  let champions: { seasons: WorldSeasonPodium[]; weeks: WorldWeekWinner[] } | null = null;
  let resolveShow: ((a: WorldScreenAction) => void) | null = null;
  let requestId = 0;

  const finish = (action: WorldScreenAction): void => {
    const done = resolveShow;
    resolveShow = null;
    hide();
    done?.(action);
  };

  // ------------------------------------------------------------ pintar

  const meHtml = (): string => {
    if (offline) {
      return `<section class="ws-box ws-me"><p class="ws-empty">${escapeHtml(t("Sin conexión. Las estadísticas mundiales viven en el servidor: vuelve a intentarlo en un momento."))}</p></section>`;
    }
    const card = myCard;
    const rating = card?.rating;
    const standing = rating ? tierFor(rating.mmr, rating.games) : null;
    const hiddenNote = service.hidden()
      ? `<p class="ws-note">🙈 ${escapeHtml(t("No sales en las clasificaciones: los demás no te ven. Tú sí te ves."))}</p>`
      : "";
    const me = board?.me;
    const position = me
      ? me.rank !== null
        ? `<div class="ws-rank"><b>#${me.rank}</b><span>${escapeHtml(t("de {n} en {metric}", { n: board!.total, metric: t(METRIC_NAME[board!.metric]) }))}</span></div>
           ${me.percentile !== null ? `<p class="ws-pct">${escapeHtml(t("Mejor que el {p} % de los clasificados", { p: me.percentile }))}</p>` : ""}
           <ul class="ws-neighbours">
             ${me.above ? `<li data-card="${me.above.clientId}">▲ ${escapeHtml(me.above.nickname)} <em>${escapeHtml(formatValue(board!.metric, me.above.value))}</em></li>` : ""}
             ${me.below ? `<li data-card="${me.below.clientId}">▼ ${escapeHtml(me.below.nickname)} <em>${escapeHtml(formatValue(board!.metric, me.below.value))}</em></li>` : ""}
           </ul>`
        : `<p class="ws-pct">${escapeHtml(
            board!.metric === "mmr"
              ? t("Para entrar: te faltan {n} partidos contra personas.", { n: me.needs })
              : me.needs > 0
                ? t("Para entrar en esta clasificación te faltan {n} partidos online.", { n: me.needs })
                : t("Aún no puntúas aquí."),
          )}</p>`
      : "";
    const tierBlock = standing
      ? standing.tier
        ? `<div class="ws-me-tier">${tierBadgeHtml(standing.tier, "l")}
             <div><strong>${escapeHtml(tierLabel(standing.tier))}</strong>
             <span>${escapeHtml(t("{mmr} puntos · {w}-{d}-{l} contra personas", { mmr: rating!.mmr, w: rating!.wins, d: rating!.draws, l: rating!.losses }))}</span></div></div>
           ${standing.toNext > 0 ? `<div class="ws-progress"><i style="width:${Math.round(standing.progress * 100)}%"></i></div><p class="ws-small">${escapeHtml(t("Te faltan {n} puntos para {tier}", { n: standing.toNext, tier: tierLabel(TIERS[TIERS.findIndex((x) => x.id === standing.tier) + 1]!.id) }))}</p>` : `<p class="ws-small">${escapeHtml(t("Lo más alto. A defenderlo."))}</p>`}`
        : `<div class="ws-me-tier">${tierBadgeHtml(null, "l")}<div><strong>${escapeHtml(t("En colocación"))}</strong><span>${escapeHtml(t("Te faltan {n} partidos contra personas para tener división.", { n: standing.placementLeft }))}</span></div></div>`
      : "";
    const played = card?.totals.matches ?? 0;
    return `
      <section class="ws-box ws-me">
        <header class="ws-box-head"><span>${escapeHtml(t("Tu sitio"))}</span>
          ${card ? `<button type="button" class="ws-link" data-card="${card.clientId}">${escapeHtml(t("Ver mi ficha"))}</button>` : ""}
        </header>
        ${tierBlock}
        ${position}
        ${hiddenNote}
        ${
          played === 0
            ? `<p class="ws-note">${escapeHtml(t("Aquí solo cuentan partidos online, y el servidor los rehace uno a uno para que nadie haga trampas. Contra la IA y la carrera no cuentan."))}</p>`
            : ""
        }
        <button type="button" class="ws-play" data-act="lobby">${escapeHtml(t("JUGAR ONLINE"))}</button>
      </section>`;
  };

  const filtersHtml = (): string => {
    const metricChips = WORLD_METRICS.map(
      (m) => `<button type="button" class="ws-chip${m === metric ? " is-on" : ""}" data-metric="${m}" aria-pressed="${m === metric}">${escapeHtml(t(METRIC_LABEL[m]))}</button>`,
    ).join("");
    const periods = WORLD_PERIODS.map(
      (p) =>
        `<button type="button" class="ws-seg${p === (metric === "mmr" ? "season" : period) ? " is-on" : ""}" data-period="${p}" ${metric === "mmr" && p !== "season" ? "disabled" : ""}>${escapeHtml(t(PERIOD_LABEL[p]))}</button>`,
    ).join("");
    const modes = [`<option value="">${escapeHtml(t("Todos los modos"))}</option>`, ...ONLINE_MODE_IDS.map((m) => `<option value="${m}"${m === mode ? " selected" : ""}>${m}</option>`)].join("");
    return `
      <div class="ws-chips" role="group" aria-label="${escapeHtml(t("Qué se clasifica"))}">${metricChips}</div>
      <div class="ws-filters">
        <div class="ws-segs" role="group">${periods}</div>
        <div class="ws-segs" role="group">
          <button type="button" class="ws-seg${friends ? "" : " is-on"}" data-scope="world">🌍 ${escapeHtml(t("Mundo"))}</button>
          <button type="button" class="ws-seg${friends ? " is-on" : ""}" data-scope="friends">👥 ${escapeHtml(t("Amigos"))}</button>
        </div>
        <select class="ws-select" data-mode ${metric === "mmr" ? "disabled" : ""} aria-label="${escapeHtml(t("Modo"))}">${modes}</select>
      </div>`;
  };

  const boardHtml = (): string => {
    if (offline) return `<p class="ws-empty">${escapeHtml(t("Sin conexión."))}</p>`;
    if (!board) return `<p class="ws-empty">${escapeHtml(t("Cargando…"))}</p>`;
    const self = service.selfId();
    const minNote =
      metric === "overall" || metric === "winRate"
        ? `<p class="ws-small">${escapeHtml(t("Con {n} partidos o más en el periodo.", { n: WORLD_MIN_MATCHES }))}</p>`
        : metric === "mmr"
          ? `<p class="ws-small">${escapeHtml(t("Solo partidos contra personas. Temporada {season}: acaba en {left}.", { season: seasonName(board.season), left: untilText(board.seasonEndsAt) }))}</p>`
          : period === "week"
            ? `<p class="ws-small">${escapeHtml(t("La semana se reinicia en {left}.", { left: untilText(board.weekEndsAt) }))}</p>`
            : "";
    if (rows.length === 0) {
      return `${minNote}<p class="ws-empty ws-empty--big">${escapeHtml(friends ? t("Ninguno de tus amigos puntúa aquí todavía.") : t("Aún no hay nadie aquí. El primero se queda el puesto uno."))}</p>`;
    }
    const body = rows
      .map((r) => {
        const medal = r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : String(r.rank);
        return `
          <li class="ws-row${r.clientId === self ? " is-me" : ""}${r.rank <= 3 ? " is-podium" : ""}" data-card="${r.clientId}" tabindex="0" role="button">
            <span class="ws-pos">${medal}</span>
            ${tierBadgeHtml(r.tier, "s")}
            <span class="ws-name">${escapeHtml(r.nickname)}</span>
            <span class="ws-matches">${escapeHtml(t("{n} PJ", { n: r.matches }))}</span>
            <b class="ws-value">${escapeHtml(formatValue(metric, r.value))}</b>
          </li>`;
      })
      .join("");
    const more = rows.length < board.total ? `<button type="button" class="ws-more" data-act="more"${loading ? " disabled" : ""}>${escapeHtml(t("Cargar más"))}</button>` : "";
    return `${minNote}<ol class="ws-list">${body}</ol>${more}`;
  };

  const recordsHtml = (): string => {
    if (offline) return `<p class="ws-empty">${escapeHtml(t("Sin conexión."))}</p>`;
    if (!records) return `<p class="ws-empty">${escapeHtml(t("Cargando…"))}</p>`;
    if (records.length === 0) return `<p class="ws-empty ws-empty--big">${escapeHtml(t("Aún no hay récords. El primer partido online los pone todos."))}</p>`;
    return `<ul class="ws-records">${records
      .map((r) => {
        const info = RECORD_LABEL[r.id];
        return `
          <li class="ws-record" data-card="${r.clientId}" tabindex="0" role="button">
            <span class="ws-record-icon" aria-hidden="true">${info.icon}</span>
            <span class="ws-record-title">${escapeHtml(t(info.title))}</span>
            <b class="ws-record-value">${escapeHtml(String(r.value))}</b>
            <span class="ws-record-who">${escapeHtml(r.nickname)} · ${escapeHtml(dateText(r.at))}${r.modeId ? ` · ${escapeHtml(r.modeId)}` : ""}</span>
          </li>`;
      })
      .join("")}</ul>`;
  };

  const championsHtml = (): string => {
    if (offline) return `<p class="ws-empty">${escapeHtml(t("Sin conexión."))}</p>`;
    if (!champions) return `<p class="ws-empty">${escapeHtml(t("Cargando…"))}</p>`;
    const medal = ["🥇", "🥈", "🥉"];
    const seasons = champions.seasons.length
      ? champions.seasons
          .map(
            (s) => `
          <li class="ws-season-card">
            <strong>${escapeHtml(seasonName(s.season))}</strong>
            <ol>${s.podium
              .map(
                (p, i) => `<li data-card="${p.clientId}" tabindex="0" role="button">${medal[i] ?? ""} ${tierBadgeHtml(p.tier, "s")} <span>${escapeHtml(p.nickname)}</span> <em>${p.mmr}</em></li>`,
              )
              .join("")}</ol>
          </li>`,
          )
          .join("")
      : `<li class="ws-empty">${escapeHtml(t("Aún no ha acabado ninguna temporada. El primer campeón saldrá a final de mes."))}</li>`;
    const weeks = champions.weeks.length
      ? champions.weeks
          .map(
            (w) => `<li data-card="${w.clientId}" tabindex="0" role="button">👑 <span>${escapeHtml(w.nickname)}</span> <em>${escapeHtml(t("semana del {d}", { d: dateText(w.weekStart) }))} · ${escapeHtml(t("{n} victorias", { n: w.wins }))}</em></li>`,
          )
          .join("")
      : `<li class="ws-empty">${escapeHtml(t("Aún ninguna semana cerrada con ganador."))}</li>`;
    return `
      <h3 class="ws-sub ws-sub--top">${escapeHtml(t("Campeones de temporada"))}</h3>
      <ul class="ws-champions">${seasons}</ul>
      <h3 class="ws-sub">${escapeHtml(t("Campeones de la semana"))}</h3>
      <ul class="ws-weeks">${weeks}</ul>`;
  };

  const tiersHtml = (): string => {
    const mine = myCard ? tierFor(myCard.rating.mmr, myCard.rating.games).tier : null;
    const ladder = [...TIERS]
      .reverse()
      .map(
        (x) => `
        <li class="ws-ladder-row${x.id === mine ? " is-me" : ""}">
          ${tierBadgeHtml(x.id, "m")}
          <strong>${escapeHtml(t(TIER_LABEL[x.id]))}</strong>
          <span>${escapeHtml(t("desde {n}", { n: x.min }))}</span>
          ${x.id === mine ? `<em>${escapeHtml(t("tú"))}</em>` : ""}
        </li>`,
      )
      .join("");
    return `
      <ol class="ws-ladder">${ladder}</ol>
      <h3 class="ws-sub">${escapeHtml(t("Premios de temporada"))}</h3>
      <ul class="ws-prizes">${COMPETITIVE_KITS.map(
        (k) => `<li>${kitTokenHtml(KITS[k.kitId], "", "m")}<div><strong>${escapeHtml(t(KITS[k.kitId]?.label ?? k.kitId))}</strong><span>${escapeHtml(
          k.champion ? t("Para el número 1 de una temporada.") : t("Acaba una temporada en {tier} o más.", { tier: tierLabel(k.tier ?? null) }),
        )}</span></div></li>`,
      ).join("")}</ul>
      <p class="ws-small">${escapeHtml(t("Se ganan para siempre y se usan en todo el juego, también online."))}</p>
      <div class="ws-explain">
        <p>${escapeHtml(t("La puntuación sube y baja con cada partido contra personas: ganar a un equipo mejor da más, perder contra uno peor quita más. Contra bots no se mueve."))}</p>
        <p>${escapeHtml(t("Los 5 primeros son de colocación. Cada mes empieza una temporada nueva y todos se acercan a 1000 a la mitad: lo ganado cuenta, pero hay que volver a demostrarlo."))}</p>
        <p>${escapeHtml(t("Cada partido lo rehace el servidor con las jugadas de todos. Si algo no cuadra, no cuenta."))}</p>
      </div>`;
  };

  const render = (): void => {
    const season = board ? `${seasonName(board.season)} · ${t("acaba en {left}", { left: untilText(board.seasonEndsAt) })}` : "";
    const tabs = (["board", "records", "champions", "tiers"] as const)
      .map(
        (id) =>
          `<button type="button" role="tab" class="ws-tab${id === tab ? " is-on" : ""}" aria-selected="${id === tab}" data-tab="${id}">${escapeHtml(
            id === "board" ? t("Clasificación") : id === "records" ? t("Récords") : id === "champions" ? t("Campeones") : t("Divisiones"),
          )}</button>`,
      )
      .join("");
    const body =
      tab === "records"
        ? recordsHtml()
        : tab === "champions"
          ? championsHtml()
          : tab === "tiers"
            ? tiersHtml()
            : filtersHtml() + `<div class="ws-board">${boardHtml()}</div>`;
    const scroll = overlay.querySelector(".ws-body")?.scrollTop ?? 0;
    overlay.innerHTML = `
      <header class="ws-top">
        <button type="button" class="ws-back" data-act="back">‹ ${escapeHtml(t("Volver"))}</button>
        <h1 class="ws-title">${escapeHtml(t("Estadísticas mundiales"))}</h1>
        ${season ? `<span class="ws-season">🏁 ${escapeHtml(season)}</span>` : ""}
        <label class="ws-visible" title="${escapeHtml(t("Aparecer en las clasificaciones"))}">
          <input type="checkbox" data-visible ${service.hidden() ? "" : "checked"} />
          <span>${escapeHtml(t("Aparecer"))}</span>
        </label>
      </header>
      <div class="ws-main">
        <div class="ws-left">${meHtml()}</div>
        <div class="ws-right">
          <nav class="ws-tabs" role="tablist">${tabs}</nav>
          <div class="ws-body">${body}</div>
        </div>
      </div>`;
    const bodyEl = overlay.querySelector(".ws-body");
    if (bodyEl) bodyEl.scrollTop = scroll;
  };

  // ------------------------------------------------------------ cargar

  const loadBoard = async (append = false): Promise<void> => {
    const id = ++requestId;
    loading = true;
    if (!append) {
      board = null;
      rows = [];
    }
    render();
    const msg = await service.board({
      metric,
      period: metric === "mmr" ? "season" : period,
      ...(mode && metric !== "mmr" ? { mode } : {}),
      ...(friends ? { friends: true } : {}),
      offset: append ? rows.length : 0,
    });
    if (id !== requestId) return;
    loading = false;
    if (!msg) {
      offline = !service.online();
      render();
      return;
    }
    offline = false;
    board = msg;
    rows = append ? [...rows, ...msg.rows] : msg.rows;
    rememberTeaser();
    render();
  };

  const loadMine = async (): Promise<void> => {
    const self = service.selfId();
    if (!self) return;
    myCard = await service.card(self);
    rememberTeaser();
    render();
  };

  const loadChampions = async (): Promise<void> => {
    champions = await service.champions();
    if (champions === null) offline = !service.online();
    render();
  };

  const loadRecords = async (): Promise<void> => {
    records = await service.records();
    if (records === null) offline = !service.online();
    render();
  };

  /** Lo que enseña la fila del menu principal sin tener que preguntar. */
  const rememberTeaser = (): void => {
    if (!myCard) return;
    const standing = tierFor(myCard.rating.mmr, myCard.rating.games);
    const rank = myCard.ranks.mmr;
    const text = standing.tier
      ? rank
        ? t("{tier} · #{rank} del mundo", { tier: tierLabel(standing.tier), rank })
        : tierLabel(standing.tier)
      : myCard.totals.matches > 0
        ? t("{n} partidos arbitrados", { n: myCard.totals.matches })
        : "";
    try {
      if (text) localStorage.setItem(WORLD_TEASER_KEY, text);
    } catch {
      // Comodidad.
    }
  };

  // ------------------------------------------------------------ la ficha

  let cardFor: string | null = null;
  let cardTab: "summary" | "form" | "badges" | "vs" = "summary";
  let cardScope: "all" | "season" | "week" = "all";
  let shownCard: WorldCard | null = null;

  const closeCard = (): void => {
    cardFor = null;
    shownCard = null;
    cardLayer.classList.add("is-hidden");
    cardLayer.innerHTML = "";
  };

  const renderCard = (): void => {
    const c = shownCard;
    if (!c) return;
    const self = service.selfId();
    const isMe = c.clientId === self;
    const standing = tierFor(c.rating.mmr, c.rating.games);
    const rate = ratePlayer(c.totals);
    const scoped = cardScope === "season" ? c.season : cardScope === "week" ? c.week : c.totals;
    const friendBtn =
      !isMe && opts.addFriend
        ? opts.isFriend?.(c.clientId)
          ? `<span class="ws-chip is-on">👥 ${escapeHtml(t("Amigos"))}</span>`
          : `<button type="button" class="ws-chip" data-act="add-friend">＋ ${escapeHtml(t("Añadir amigo"))}</button>`
        : "";
    const ranks = (Object.entries(c.ranks) as Array<[WorldMetric, number]>)
      .sort((a, b) => a[1] - b[1])
      .slice(0, 4)
      .map(([m, r]) => `<span class="ws-rank-chip">#${r} ${escapeHtml(t(METRIC_NAME[m]))}</span>`)
      .join("");
    const tabs = (["summary", "form", "badges", ...(isMe ? [] : ["vs"])] as const)
      .map(
        (id) =>
          `<button type="button" role="tab" class="ws-tab${id === cardTab ? " is-on" : ""}" data-card-tab="${id}">${escapeHtml(
            id === "summary" ? t("Resumen") : id === "form" ? t("Forma") : id === "badges" ? t("Insignias") : t("Cara a cara"),
          )}</button>`,
      )
      .join("");

    let body = "";
    if (cardTab === "summary") {
      const passPct = Math.round(per(scoped.passesOk, scoped.passes) * 100);
      const scopes = (["all", "season", "week"] as const)
        .map((s) => `<button type="button" class="ws-seg${s === cardScope ? " is-on" : ""}" data-card-scope="${s}">${escapeHtml(t(s === "all" ? "Siempre" : s === "season" ? "Temporada" : "Semana"))}</button>`)
        .join("");
      body = `
        <div class="ws-segs">${scopes}</div>
        <div class="ws-tiles">
          <div><b>${scoped.matches}</b><span>${escapeHtml(t("Partidos"))}</span></div>
          <div><b>${scoped.wins}-${scoped.draws}-${scoped.losses}</b><span>${escapeHtml(t("G-E-P"))}</span></div>
          <div><b>${scoped.goals}</b><span>${escapeHtml(t("Goles"))}</span></div>
          <div><b>${scoped.assists}</b><span>${escapeHtml(t("Asistencias"))}</span></div>
          <div><b>${scoped.mvps}</b><span>MVP</span></div>
          <div><b>${scoped.matches ? (scoped.goals / scoped.matches).toFixed(1) : "0"}</b><span>${escapeHtml(t("Goles/partido"))}</span></div>
          <div><b>${passPct} %</b><span>${escapeHtml(t("Pases buenos"))}</span></div>
          <div><b>${scoped.recoveries}</b><span>${escapeHtml(t("Robos"))}</span></div>
        </div>
        <h3 class="ws-sub">${escapeHtml(t("Mejores marcas"))}</h3>
        <div class="ws-tiles ws-tiles--4">
          <div><b>${c.best.goals}</b><span>${escapeHtml(t("Goles en un partido"))}</span></div>
          <div><b>+${c.best.biggestWin}</b><span>${escapeHtml(t("Mayor goleada"))}</span></div>
          <div><b>${c.best.score}</b><span>${escapeHtml(t("Mejor partido"))}</span></div>
          <div><b>${c.best.winStreak}</b><span>${escapeHtml(t("Mejor racha"))}</span></div>
        </div>
        ${
          c.modes.length
            ? `<h3 class="ws-sub">${escapeHtml(t("Por modo"))}</h3><table class="ws-table"><tbody>${c.modes
                .map((m) => `<tr><td>${escapeHtml(m.modeId)}</td><td>${escapeHtml(t("{n} PJ", { n: m.matches }))}</td><td>${Math.round(per(m.wins, m.matches) * 100)} %</td><td>⚽ ${m.goals}</td><td>🎯 ${m.assists}</td></tr>`)
                .join("")}</tbody></table>`
            : ""
        }
        ${
          c.pastSeasons.length
            ? `<h3 class="ws-sub">${escapeHtml(t("Temporadas pasadas"))}</h3><ul class="ws-seasons">${c.pastSeasons
                .map((s) => `<li>${tierBadgeHtml(tierFor(s.mmr, s.games).tier, "s")} ${escapeHtml(seasonName(s.season))} · ${s.mmr} · #${s.rank}</li>`)
                .join("")}</ul>`
            : ""
        }`;
    } else if (cardTab === "form") {
      body = c.recent.length
        ? `<div class="ws-form">${c.recent.map((r) => `<i class="is-${r.result}" title="${r.goalsFor}-${r.goalsAgainst}"></i>`).join("")}</div>
           <ul class="ws-recent">${c.recent
             .map(
               (r) => `<li class="is-${r.result}">
                 <b>${r.goalsFor}-${r.goalsAgainst}</b>
                 <span>${escapeHtml(r.modeId)} · ${escapeHtml(dateText(r.at))} · ${escapeHtml(r.vsHumans ? t("contra personas") : t("contra bots"))}</span>
                 <em>${r.goals ? `⚽${r.goals} ` : ""}${r.assists ? `🎯${r.assists} ` : ""}${r.mvp ? "⭐" : ""}</em>
               </li>`,
             )
             .join("")}</ul>`
        : `<p class="ws-empty">${escapeHtml(t("Sin partidos todavía."))}</p>`;
    } else if (cardTab === "badges") {
      body = c.badges.length
        ? `<ul class="ws-badges">${c.badges
            .map((b) => {
              const info = badgeInfo(b);
              return `<li class="ws-badge"${b.tier ? ` style="--tier:${tierColor(b.tier)}"` : ""}><span class="ws-badge-icon">${info.icon}</span><div><strong>${escapeHtml(info.title)}</strong><span>${escapeHtml(info.desc)}</span></div></li>`;
            })
            .join("")}</ul>`
        : `<p class="ws-empty">${escapeHtml(t("Aún ninguna. Se ganan jugando online: rachas, récords, top 10, campeón de la semana…"))}</p>`;
    } else if (myCard) {
      const rows = [
        { label: t("puntuación"), a: c.rating.mmr, b: myCard.rating.mmr },
        { label: t("la media"), a: ratePlayer(c.totals).overall, b: ratePlayer(myCard.totals).overall },
        { label: t("Goles/partido"), a: per(c.totals.goals, c.totals.matches), b: per(myCard.totals.goals, myCard.totals.matches), dec: true },
        { label: t("Asist./partido"), a: per(c.totals.assists, c.totals.matches), b: per(myCard.totals.assists, myCard.totals.matches), dec: true },
        { label: t("% victorias"), a: Math.round(per(c.totals.wins, c.totals.matches) * 100), b: Math.round(per(myCard.totals.wins, myCard.totals.matches) * 100) },
        { label: t("Mejor racha"), a: c.best.winStreak, b: myCard.best.winStreak },
      ];
      body = `${radarHtml(c.totals, myCard.totals, c.nickname)}
        <ul class="ws-vs">${rows
          .map((r) => {
            const max = Math.max(r.a, r.b, 0.0001);
            const fmt = (v: number): string => (r.dec ? v.toFixed(2) : String(Math.round(v)));
            return `<li><span class="ws-vs-a${r.a > r.b ? " is-best" : ""}">${fmt(r.a)}</span>
              <i class="ws-vs-bar"><b class="is-a" style="width:${(r.a / max) * 50}%"></b><b class="is-b" style="width:${(r.b / max) * 50}%"></b></i>
              <span class="ws-vs-b${r.b > r.a ? " is-best" : ""}">${fmt(r.b)}</span>
              <em>${escapeHtml(r.label)}</em></li>`;
          })
          .join("")}</ul>`;
    } else {
      body = `<p class="ws-empty">${escapeHtml(t("Juega online para poder compararte."))}</p>`;
    }

    cardLayer.innerHTML = `
      <div class="ws-card-backdrop" data-act="close-card"></div>
      <article class="ws-card">
        <header class="ws-card-head">
          ${tierBadgeHtml(standing.tier, "l")}
          <div class="ws-card-id">
            <strong>${escapeHtml(c.nickname)}${isMe ? ` <em>(${escapeHtml(t("tú"))})</em>` : ""}</strong>
            <span>${escapeHtml(tierLabel(standing.tier))} · ${escapeHtml(t("{mmr} puntos", { mmr: c.rating.mmr }))}${c.rating.peak > c.rating.mmr ? ` · ${escapeHtml(t("pico {n}", { n: c.rating.peak }))}` : ""}</span>
            <div class="ws-rank-chips">${ranks}</div>
          </div>
          <div class="ws-ovr"><b>${c.totals.matches > 0 ? rate.overall : "–"}</b><span>${escapeHtml(t("MEDIA"))}</span></div>
          <button type="button" class="ws-close" data-act="close-card" aria-label="${escapeHtml(t("Cerrar"))}">✕</button>
        </header>
        <div class="ws-card-actions">${friendBtn}${c.totals.matches > 0 ? `<span class="ws-arch">${escapeHtml(rate.archetype)} · ${escapeHtml(rate.archetypeHint)}</span>` : ""}</div>
        ${cardTab === "summary" && c.totals.matches > 0 ? radarHtml(c.totals, null, c.nickname) : ""}
        <nav class="ws-tabs" role="tablist">${tabs}</nav>
        <div class="ws-card-body">${body}</div>
      </article>`;
    cardLayer.querySelector<HTMLElement>(".ws-close")?.focus({ preventScroll: true });
  };

  const openCard = async (clientId: string): Promise<void> => {
    cardFor = clientId;
    cardTab = "summary";
    cardScope = "all";
    shownCard = null;
    cardLayer.classList.remove("is-hidden");
    cardLayer.innerHTML = `<div class="ws-card-backdrop" data-act="close-card"></div><article class="ws-card"><p class="ws-empty">${escapeHtml(t("Cargando…"))}</p></article>`;
    const card = clientId === service.selfId() && myCard ? myCard : await service.card(clientId);
    if (cardFor !== clientId) return;
    if (!card) {
      cardLayer.innerHTML = `<div class="ws-card-backdrop" data-act="close-card"></div><article class="ws-card"><p class="ws-empty">${escapeHtml(
        card === null ? t("Esta ficha no es pública.") : t("Sin conexión."),
      )}</p><button type="button" class="ws-more" data-act="close-card">${escapeHtml(t("Cerrar"))}</button></article>`;
      return;
    }
    shownCard = card;
    renderCard();
  };

  // ------------------------------------------------------------ eventos

  overlay.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const el = target.closest<HTMLElement>("[data-act],[data-tab],[data-metric],[data-period],[data-scope],[data-card]");
    if (!el) return;
    if (el.dataset.act === "back") return finish({ type: "back" });
    if (el.dataset.act === "lobby") return finish({ type: "lobby" });
    if (el.dataset.act === "more") return void loadBoard(true);
    if (el.dataset.tab) {
      tab = el.dataset.tab as Tab;
      if (tab === "records" && !records) void loadRecords();
      if (tab === "champions" && !champions) void loadChampions();
      render();
      return;
    }
    if (el.dataset.metric) {
      metric = el.dataset.metric as WorldMetric;
      return void loadBoard();
    }
    if (el.dataset.period && !(el as HTMLButtonElement).disabled) {
      period = el.dataset.period as WorldPeriod;
      return void loadBoard();
    }
    if (el.dataset.scope) {
      friends = el.dataset.scope === "friends";
      return void loadBoard();
    }
    if (el.dataset.card) return void openCard(el.dataset.card);
  });
  overlay.addEventListener("change", (event) => {
    const target = event.target as HTMLElement;
    if (target instanceof HTMLSelectElement && target.dataset.mode !== undefined) {
      mode = (target.value || undefined) as OnlineModeId | undefined;
      void loadBoard();
    }
    if (target instanceof HTMLInputElement && target.dataset.visible !== undefined) {
      service.setHidden(!target.checked);
      void loadBoard();
    }
  });
  overlay.addEventListener("keydown", (event) => {
    const el = (event.target as HTMLElement).closest<HTMLElement>("[data-card]");
    if (el && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      void openCard(el.dataset.card!);
    }
  });
  cardLayer.addEventListener("click", (event) => {
    const el = (event.target as HTMLElement).closest<HTMLElement>("[data-act],[data-card-tab],[data-card-scope]");
    if (!el) return;
    if (el.dataset.act === "close-card") return closeCard();
    if (el.dataset.act === "add-friend" && shownCard) {
      opts.addFriend?.(shownCard.clientId);
      el.textContent = t("Solicitud enviada");
      (el as HTMLButtonElement).disabled = true;
      return;
    }
    if (el.dataset.cardTab) {
      cardTab = el.dataset.cardTab as typeof cardTab;
      renderCard();
      return;
    }
    if (el.dataset.cardScope) {
      cardScope = el.dataset.cardScope as typeof cardScope;
      renderCard();
    }
  });

  const onKey = (event: KeyboardEvent): void => {
    if (event.key !== "Escape" || overlay.classList.contains("is-hidden")) return;
    event.preventDefault();
    if (!cardLayer.classList.contains("is-hidden")) closeCard();
    else finish({ type: "back" });
  };
  window.addEventListener("keydown", onKey);

  function hide(): void {
    overlay.classList.add("is-hidden");
    closeCard();
  }

  return {
    show() {
      overlay.classList.remove("is-hidden");
      offline = !service.online();
      tab = "board";
      render();
      void loadMine();
      void loadBoard();
      overlay.querySelector<HTMLElement>("[data-act='back']")?.focus({ preventScroll: true });
      return new Promise((resolve) => {
        resolveShow = resolve;
      });
    },
    hide,
    leave: () => finish({ type: "back" }),
    dispose() {
      window.removeEventListener("keydown", onKey);
      overlay.remove();
      cardLayer.remove();
    },
  };
}
